/* eslint-disable no-console */
/* eslint-disable max-len */
/* eslint-disable quotes */
/* eslint-disable camelcase */
/* eslint-disable lines-between-class-members */
/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import PromiseSource from './PromiseSource';
import { ZENDESK_AUTH_CHANNEL, ZENDESK_AUTH_CLOSE_MESSAGE } from './AuthConsts';

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

export interface ZendeskUserInfo {
  id: number;
  name: string;
  email: string;
}

interface OrganizationMembership {
    url: string;
    organization_id: number;
    user_id: number;
    default: boolean | null;
    created_at: string;
    updated_at: string;
    organization_name: string;
    view_tickets: boolean;
}

interface OrganizationResponse {
    organization_memberships: OrganizationMembership[];
    next_page: number | null;
    prevoius_page: number | null;
    count: number;
}

export interface OrganizationInfo {
    name: string;
    id: number;
}

async function showLoginPopup(url: string): Promise<string> {
  const popUp = window.open(url, 'zendeskLogin', 'width=970,height=530');
  if (!popUp) {
    // eslint-disable-next-line no-alert
    alert("It looks like you've activated a pop-up blocker. Consider disabling it for this page to allow login process.");
    console.warn('May not open popups here! Consider disabling popup blocker for this page.');
    return Promise.reject(new Error('Popup blocked'));
  }

  const promiseSource = new PromiseSource<string>();
  const channel = new BroadcastChannel(ZENDESK_AUTH_CHANNEL);

  const onMessage = (evt: MessageEvent<any>): any => {
    if (evt.origin !== window.location.origin) {
      console.warn("Received message from unknown origin:", evt.origin);
      return;
    }

    channel.postMessage(ZENDESK_AUTH_CLOSE_MESSAGE);
    popUp.close();
    channel.close();

    const code = evt.data as string;
    promiseSource.resolve(code);
  };

  channel.onmessage = onMessage;

  return promiseSource.promise;
}

export class ZendeskOauthClient {
  private readonly host:string;

  private _isLoggedIn: boolean = false;
  private _token: string | null = null;

  public get isLoggedIn(): boolean {
    return this._isLoggedIn;
  }

  public get accessToken(): string | null {
    return this._token;
  }

  constructor(host: string, isLoggedIn: boolean = false, token: string | null = null) {
    this.host = host;
    this._isLoggedIn = isLoggedIn;
    this._token = token;
  }

  public async login(clientId: string): Promise<boolean> {
    try {
      const codeVerifier = generateRandomString(128);
      const codeChallenge = await sha256(codeVerifier);
      const currentLocation = window.location.origin;
      const redirectUri = `${currentLocation}/auth-token`;
      const encodedRedirectUri = encodeURIComponent(redirectUri);

      const accessCode = await this.requestAccessCode(clientId, codeChallenge, encodedRedirectUri);
      const accessPromise = this.requestAccessToken(clientId, accessCode, codeVerifier, redirectUri);
      this._token = await accessPromise;
      this._isLoggedIn = true;
      return true;
    } catch (reason: any) {
      console.warn('Error during Zendesk OAuth login:', reason);
      return false;
    }
  }

  public async requestAccessCode(clientId: string, codeChallenge: string, redirectUri: string): Promise<string> {
    const responseType = 'code';
    const scope = 'read';
    const codeChallengeMethod = 'S256';

    const state = window.location.search;

    const zendeskParams = `response_type=${responseType}&redirect_uri=${redirectUri}&client_id=${clientId}&scope=${scope}&code_challenge=${codeChallenge}&code_challenge_method=${codeChallengeMethod}&state=${encodeURIComponent(state)}`;
    const zendeskUrl = `${this.host}/oauth/authorizations/new?${zendeskParams}`;

    return showLoginPopup(zendeskUrl);
  }

  public async requestAccessToken(clientId: string, code: string, verifier: string, redirectUri: string): Promise<string> {
    // This was the actual URL for Zendesk OAuth token exchange, but due to CORS limitations on Zendesk side, we use the proxy server
    // const url = `${this.host}/oauth/tokens`;
    console.debug(`Using ${window.location.origin}/zendesk/oauth/tokens as proxy for ${this.host}`);
    const url = `${window.location.origin}/zendesk/oauth/tokens`;

    const payload = {
      grant_type: "authorization_code",
      code,
      client_id: clientId,
      code_verifier: verifier,
      redirect_uri: redirectUri,
    };

    const config = { headers: { "Content-Type": "application/json" } };
    const response = await axios.post(url, payload, config);
    return (response.data as TokenResponse).access_token;
  }

  public async getUserInfo(): Promise<ZendeskUserInfo> {
    if (!this._isLoggedIn || !this._token) {
      throw new Error('Not logged in or token is missing');
    }

    const url = `${this.host}/api/v2/users/me`;
    const config = {
      headers: {
        Authorization: `Bearer ${this._token}`,
        'Content-Type': 'application/json',
      },
    };

    try {
      const response = await axios.get(url, config);
      return response.data.user;
    } catch (error) {
      console.error('Error fetching user info:', error);
      throw error;
    }
  }

  public async getUserOrganizations(id: number): Promise<OrganizationInfo[]> {
    if (!this._isLoggedIn || !this._token) {
      throw new Error('Not logged in or token is missing');
    }

    // const url = `${this.host}/api/v2/users/${id}/organization_memberships`;
    const url = `${window.location.origin}/zendesk/users/${id}/organization_memberships`;
    const config = {
      headers: {
        Authorization: `Bearer ${this._token}`,
      },
    };

    try {
      const response = await axios.get(url, config);
      const memberships = response.data as OrganizationResponse;
      return memberships.organization_memberships
        .filter((m) => m.view_tickets)
        .map((m) => ({ name: m.organization_name, id: m.organization_id } as OrganizationInfo));
    } catch (error) {
      console.error('Error fetching user organizations:', error);
      throw error;
    }
  }
}

// Function to generate a random string of given length
export function generateRandomString(length: number): string {
  const buffer = new Uint8Array(length);
  window.crypto.getRandomValues(buffer);
  return btoa(String.fromCharCode.apply(null, Array.from(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Function to generate SHA-256 hash and encode it in base64
export async function sha256(base64String: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(base64String);
  const hash = await window.crypto.subtle.digest('SHA-256', data);
  let binary = '';
  const bytes = new Uint8Array(hash);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
